<script setup lang="ts">
import { ref } from "vue"
import { useRouter } from "vue-router"
import { $date, useHttpGet } from "@/vf"
import type { PickupAuthorization } from "@/model/backend/pickup-authorization.ts"
import CreatePickupAuthorization from "@/shop/components/Authorization/CreatePickupAuthorization.vue"

const router = useRouter()

type WizardStep = "intro" | "isOrderSpecificPickupAuthorization" | "create"
const step = ref<WizardStep>("intro")

const { data: pickupAuthorizations, refresh } = useHttpGet<PickupAuthorization[]>("pickup-authorization", false)

const isSaving = ref(false)
const hasCreated = ref(false)
const createRef = ref<InstanceType<typeof CreatePickupAuthorization> | null>(null)

const isOrderSpecificPickupAuthorization = ref(false)
function setIsOrderSpecificAuthorization(value: boolean) {
    isOrderSpecificPickupAuthorization.value = value
    step.value = "create"
}

async function handleSave() {
    await createRef.value?.save()
    await refresh()
    if (hasCreated.value) {
        await router.push({ name: "prescription" })
    }
}
</script>

<template>
    <div v-if="step === 'intro'" class="mt-3">
        <div v-if="pickupAuthorizations?.length">
            Folgende Personen hast du bereits für die Abholung deiner Bestellung bevollmächtigt:
        </div>

        <div class="row">
            <div v-for="authorization in pickupAuthorizations" :key="authorization.id" class="col-md-6 col-12 mb-2">
                <div class="authorization-card h-100">
                    <div class="d-flex h-100">
                        <div class="d-flex align-items-center justify-content-center mr-3">
                            <i class="fa-regular fa-id-card"></i>
                        </div>
                        <div class="align-self-center justify-content-start">
                            <div>{{ authorization.lastName }}, {{ authorization.firstName }}</div>
                            <div>geboren am {{ $date(authorization.dateOfBirth) }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="pickupAuthorizations && pickupAuthorizations!.length > 0" class="ml-3 mt-3">
            Du kannst bestehende Vollmachten in deinem Kundenprofil löschen.
        </div>

        <div class="mt-4">Möchtest du eine neue Vollmacht erteilen?</div>
        <button class="wizard-button" @click="router.push({ name: 'prescription' })">
            <div class="d-flex justify-content-between">Nein, keine neue Vollmacht erteilen.</div>
        </button>
        <button class="wizard-button" @click="step = 'isOrderSpecificPickupAuthorization'">
            <div class="d-flex justify-content-between">Ja, neue Vollmacht erteilen.</div>
        </button>
    </div>

    <div v-if="step === 'isOrderSpecificPickupAuthorization'" class="mt-3">
        <button class="wizard-button" @click="setIsOrderSpecificAuthorization(true)">
            Nur für diese Bestellung erteilen
        </button>
        <button class="wizard-button" @click="setIsOrderSpecificAuthorization(false)">
            Für diese und zukünftige Bestellungen erteilen
        </button>
    </div>

    <div v-if="step === 'create'" class="mt-3">
        <CreatePickupAuthorization
            ref="createRef"
            :is-order-specific-pickup-authorization="isOrderSpecificPickupAuthorization"
            @created="hasCreated = true"
            @cancel="step = 'intro'"
            @saving="isSaving = $event"
        />

        <div class="mt-4 d-flex gap-2 justify-content-between">
            <button type="button" class="btn btn-outline-secondary" @click="step = 'intro'">Zurück</button>
            <button type="button" :disabled="isSaving" class="btn btn-primary" @click="handleSave()">
                {{ isSaving ? "Wird gespeichert..." : "Speichern" }}
            </button>
        </div>
    </div>
</template>

<style scoped lang="scss">
@import "@/shop/assets/style/variables";

.authorization-card {
    display: flex;
    align-items: center;
    gap: 1rem;
    height: 100%;
    padding: 1rem 1.25rem;
    margin: 0.75rem 0;
    background-color: rgba($primary, 0.06);
    border-left: 4px solid $primary;
    border-top: 1px solid $primary;
    border-bottom: 1px solid $primary;
    border-right: 1px solid $primary;
    border-radius: 0.5rem;
}
</style>
